import { useQuery } from '@tanstack/react-query';
import type { WhatsappBalance } from '../../types/whatsappBalance';
import sessionService from '../../services/session.service';
import messageTemplateService from '../../services/messageTemplate.service';
import assistantService from '../../services/assistant.service';
import {
  Form,
  FormField,
  FormLabel,
  FormSelect,
  Button,
  LoadingSpinner,
} from '../ui';

interface WhatsappBalanceFormProps {
  balance: WhatsappBalance | null;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  isLoading: boolean;
  onClose: () => void;
}

function WhatsappBalanceForm({
  balance,
  onSubmit,
  isLoading,
  onClose,
}: WhatsappBalanceFormProps) {
  const { data: sessions, isLoading: loadingSessions } = useQuery({
    queryKey: ['sessions'],
    queryFn: () => sessionService.getSessions(),
  });

  const { data: templates, isLoading: loadingTemplates } = useQuery({
    queryKey: ['message-templates'],
    queryFn: async () => {
      const userTemplates = await messageTemplateService.getUserTemplates();
      const globalTemplates = await messageTemplateService.getGlobalTemplates();
      return [...userTemplates, ...globalTemplates];
    },
  });

  const { data: assistants, isLoading: loadingAssistants } = useQuery({
    queryKey: ['assistants'],
    queryFn: () => assistantService.getAssistants(),
  });

  const isLoadingData = loadingSessions || loadingTemplates || loadingAssistants;

  if (isLoadingData) {
    return (
      <div className="flex justify-center items-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <Form onSubmit={onSubmit}>
      <FormField>
        <FormLabel htmlFor="whatsapp_id" required>
          Conexão
        </FormLabel>
        <FormSelect
          id="whatsapp_id"
          name="whatsapp_id"
          defaultValue={balance?.whatsapp_id || ''}
          required
        >
          <option value="">Selecione uma conexão</option>
          {sessions?.map(session => (
            <option key={session.id} value={session.id}>
              {session.session_name}
            </option>
          ))}
        </FormSelect>
      </FormField>

      <FormField>
        <FormLabel htmlFor="template_id">Template de Mensagem</FormLabel>
        <FormSelect
          id="template_id"
          name="template_id"
          defaultValue={balance?.template_id || ''}
        >
          <option value="">Nenhum</option>
          {templates?.map(template => (
            <option key={template.id} value={template.id}>
              {template.category_description}
            </option>
          ))}
        </FormSelect>
      </FormField>

      <FormField>
        <FormLabel htmlFor="assistant_id">Assistente</FormLabel>
        <FormSelect
          id="assistant_id"
          name="assistant_id"
          defaultValue={balance?.assistant_id || ''}
        >
          <option value="">Nenhum</option>
          {assistants?.map(assistant => (
            <option key={assistant.id} value={assistant.id}>
              {assistant.name}
            </option>
          ))}
        </FormSelect>
      </FormField>

      <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
        <Button type="submit" isLoading={isLoading} className="sm:col-start-2">
          {balance ? 'Salvar' : 'Criar'}
        </Button>
        <Button type="button" variant="secondary" onClick={onClose} className="sm:col-start-1">
          Cancelar
        </Button>
      </div>
    </Form>
  );
}

export default WhatsappBalanceForm;
